/**
 * Workspace context provider managing active workspace state,
 * agent selection, and workspace-level settings.
 *
 * IMPERSONATION (SPEC-FRONTEND-RULE-095(3), blocking finding B2): while a
 * binding is live the provider NEVER keeps the impersonator's selection. It
 * mounts fresh under the identity key, reads the TARGET's own workspace list,
 * selects one of them, and - when the target belongs to none - renders the
 * no-workspace EMPTY STATE instead of an endless skeleton. Until a workspace is
 * selected every workspace-scoped route answers 403 (SPEC-AUTHZ-PDP-RULE-006
 * admits no fallback); that is the ruled behaviour, not a bug.
 *
 * @module store/WorkspaceContext
 * @see TASK-FRONTEND-WS-STATE-CHG-002
 * @see TASK-IMP-AUTHCONTEXT-SPA-WIRING-CHG-018
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box } from "@mui/material";
import { useLingui } from "@lingui/react/macro";
import {
  type Agent,
  type Workspace,
  type WorkspaceSettings,
  getWorkspace,
  getWorkspaceSettings,
  listWorkspaces,
  updateWorkspaceSettings,
  invalidateWorkspaceCache,
} from "../api/workspace";
import { setWorkspaceIdGetter } from "../api/client";
import { usePermissionContext } from "./PermissionContext";
import { getActAsSessionRef } from "../api/impersonation";
import { EmptyState } from "../components/shared/EmptyState";
import { IMPERSONATION_BANNER_MIN_HEIGHT } from "../components/shared/ImpersonationBanner";

// ---------------------------------------------------------------------------
// Persisted selection
// ---------------------------------------------------------------------------

/**
 * Where the chosen workspace is remembered across reloads
 * (TASK-BUGFIX-AGENTS-WORKSPACE-SCOPE, ruled by Tibor 2026-09-14 / D-311).
 *
 * THIS IS A UI PREFERENCE, NEVER AN AUTHORIZATION INPUT. The server derives
 * the tenant from the verified principal and re-checks the workspace against
 * that tenant on every request, so a tampered value can only ever produce a
 * 404 - it cannot widen what this browser may see. It is also NOT the
 * "default workspace" that HLD-010 D-2 retired: that retirement bans an
 * authorization fallback, and nothing here decides permissions.
 */
const ACTIVE_WORKSPACE_STORAGE_KEY = "verisa-active-workspace-id";

/**
 * Read the remembered workspace id.
 *
 * Storage can be absent or throw outright - a private window, cleared or
 * blocked site data - so every access is guarded and a failure degrades to
 * "nothing remembered" rather than breaking the provider.
 */
function readPersistedWorkspaceId(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Remember the workspace id, or forget it when passed null. */
function writePersistedWorkspaceId(id: string | null): void {
  try {
    if (id === null) {
      window.localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY);
    } else {
      window.localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, id);
    }
  } catch {
    // Non-fatal: the selection simply will not survive this reload.
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkspaceContextValue {
  /** The active workspace, or null while loading. */
  workspace: Workspace | null;
  /** All workspaces visible to the tenant — used by the header switcher. */
  workspaces: Workspace[];
  /** Workspace settings. */
  settings: WorkspaceSettings | null;
  /** Currently selected agent, or null if none selected. */
  selectedAgent: Agent | null;
  /** Whether workspace data is loading. */
  loading: boolean;
  /** Any error from loading workspace data. */
  error: Error | null;
  /** Error from loading the workspace list (non-fatal — switcher stays functional with empty list). */
  listError: Error | null;
  /** Select an agent for conversation. */
  selectAgent: (agent: Agent | null) => void;
  /** Switch the active workspace by id. */
  switchWorkspace: (id: string) => void;
  /** Update workspace settings. */
  updateSettings: (settings: Partial<WorkspaceSettings>) => Promise<void>;
  /** Force reload workspace data from API. */
  refresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

/**
 * Hook to access the workspace context.
 */
export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ctx;
}

/**
 * The active workspace's id, or undefined - also outside a WorkspaceProvider,
 * for shared components that render in the admin SPA too (the model picker).
 * A view input only (D-311), never an authorization one.
 */
export function useActiveWorkspaceId(): string | undefined {
  return useContext(WorkspaceContext)?.workspace?.id;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface WorkspaceProviderProps {
  children: React.ReactNode;
}

export function WorkspaceProvider({
  children,
}: WorkspaceProviderProps): React.ReactElement {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [listError, setListError] = useState<Error | null>(null);
  /** True when a live binding's TARGET belongs to no workspace at all. */
  const [noWorkspace, setNoWorkspace] = useState(false);
  const { t } = useLingui();
  // Read from the synchronous act-as getter: this provider mounts FRESH under
  // the identity key on every arm and exit (RULE-095(2)), so the value read at
  // render is the value for this provider's whole life.
  const impersonated = getActAsSessionRef() !== null;

  /**
   * The impersonated load (RULE-095(3)): the TARGET's own workspace list first
   * (the request already carries the target's tenant and the act-as header),
   * then a selection from it - never the impersonator's, never the server's
   * auto-provisioning default, which a live binding refuses (404).
   */
  const loadImpersonatedWorkspace = useCallback(async () => {
    setLoading(true);
    setError(null);
    setListError(null);
    setNoWorkspace(false);
    setWorkspace(null);
    invalidateWorkspaceCache();
    try {
      const wsList = await listWorkspaces();
      setWorkspaces(wsList);
      if (wsList.length === 0) {
        setNoWorkspace(true);
        return;
      }
      const selected = wsList.find((w) => w.is_default) ?? wsList[0];
      setWorkspace(selected);
      // Settings are workspace-scoped, so they are read AFTER the selection
      // is stamped; a failure here is not a failure to select.
      try {
        setSettings(await getWorkspaceSettings());
      } catch (err) {
        console.error("[WorkspaceContext] Failed to load workspace settings:", err);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load workspace"));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWorkspace = useCallback(async () => {
    if (impersonated) {
      await loadImpersonatedWorkspace();
      return;
    }
    setLoading(true);
    setError(null);
    setListError(null);
    try {
      // Fetch active workspace + settings + the full list in parallel.
      // The list feeds the header workspace switcher; without it the
      // switcher button stays disabled (TenantHeader.tsx:238).
      const [ws, wsSettings, wsList] = await Promise.all([
        getWorkspace(),
        getWorkspaceSettings(),
        listWorkspaces().catch((err) => {
          console.error("[WorkspaceContext] Failed to load workspace list:", err);
          setListError(err instanceof Error ? err : new Error("Failed to load workspace list"));
          return [] as Workspace[];
        }),
      ]);
      // HONOUR THE REMEMBERED SELECTION, BUT ONLY IF IT IS STILL REAL.
      //
      // `ws` is whatever GET /api/v1/workspace returns, which today is the
      // tenant's FIRST workspace with no pinned ordering
      // (handleGetCurrentWorkspace, PerPage: 1). Without this the switcher
      // would snap back to an arbitrary workspace on every reload.
      //
      // The membership test against wsList is what makes this safe: a
      // remembered id that is not in the list the API just returned FOR THIS
      // TENANT is discarded and cleared. That covers the tenant switch, the
      // deleted workspace, and a shared browser - none of which may resurrect
      // a stale selection.
      const persistedId = readPersistedWorkspaceId();
      const persisted = persistedId
        ? wsList.find((candidate) => candidate.id === persistedId)
        : undefined;
      if (persistedId && !persisted) {
        writePersistedWorkspaceId(null);
      }
      setWorkspace(persisted ?? ws);
      setSettings(wsSettings);
      setWorkspaces(wsList);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load workspace"));
    } finally {
      setLoading(false);
    }
  }, [impersonated, loadImpersonatedWorkspace]);

  const switchWorkspace = useCallback(
    (id: string) => {
      const target = workspaces.find((w) => w.id === id);
      if (!target || target.id === workspace?.id) return;
      // Optimistically update the active workspace; permission refetch
      // happens in the existing effect that watches workspace?.id.
      setWorkspace(target);
      // Remember it so a reload does not fall back to the server's
      // "first workspace" answer (TASK-BUGFIX-AGENTS-WORKSPACE-SCOPE).
      writePersistedWorkspaceId(target.id);
      invalidateWorkspaceCache();
    },
    [workspace?.id, workspaces],
  );

  const refresh = useCallback(async () => {
    invalidateWorkspaceCache();
    await loadWorkspace();
  }, [loadWorkspace]);

  const selectAgent = useCallback((agent: Agent | null) => {
    setSelectedAgent(agent);
  }, []);

  const handleUpdateSettings = useCallback(
    async (partial: Partial<WorkspaceSettings>) => {
      const updated = await updateWorkspaceSettings(partial);
      setSettings(updated);
    },
    [],
  );

  const getWorkspaceId = useCallback(() => workspace?.id ?? null, [workspace]);
  const { refetchPermissions } = usePermissionContext();

  useEffect(() => {
    setWorkspaceIdGetter(getWorkspaceId);
  }, [getWorkspaceId]);

  // Belt and braces on the way OUT of a binding too: the hand-rolled cache is
  // already tenant+workspace scoped, and this subtree unmounts on exit.
  useEffect(() => {
    if (!impersonated) return undefined;
    return () => invalidateWorkspaceCache();
  }, [impersonated]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  // Refetch permissions when workspace changes so workspace-scoped
  // permissions (custom roles, overrides) stay in sync.
  useEffect(() => {
    if (workspace?.id) {
      refetchPermissions();
    }
  }, [workspace?.id, refetchPermissions]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspace,
      workspaces,
      settings,
      selectedAgent,
      loading,
      error,
      listError,
      selectAgent,
      switchWorkspace,
      updateSettings: handleUpdateSettings,
      refresh,
    }),
    [
      workspace,
      workspaces,
      settings,
      selectedAgent,
      loading,
      error,
      listError,
      selectAgent,
      switchWorkspace,
      handleUpdateSettings,
      refresh,
    ],
  );

  // THE NO-WORKSPACE EMPTY STATE and the loud failure, inside a binding only
  // (EPIC-IMPERSONATION-AC-002). The un-impersonated path is unchanged: the
  // server auto-provisions there, so it never meets either case. The banner
  // above stays mounted, so the exit remains one click away.
  let impersonatedBody: React.ReactNode = null;
  if (impersonated && !loading && noWorkspace) {
    impersonatedBody = (
      <EmptyState
        variant="generic"
        title={t`This user has no workspace`}
        description={t`The user you are viewing belongs to no workspace, so there is nothing to show here. Exit the view to go back.`}
      />
    );
  } else if (impersonated && !loading && error) {
    impersonatedBody = (
      <EmptyState
        variant="error"
        title={t`The user's workspaces could not be loaded`}
        description={t`Nothing is shown until they load, so that no other workspace is used by mistake.`}
        actionText={t`Retry`}
        onAction={() => void loadWorkspace()}
      />
    );
  }

  return (
    <WorkspaceContext.Provider value={value}>
      {impersonatedBody ? (
        <Box sx={{ pt: `${IMPERSONATION_BANNER_MIN_HEIGHT}px` }} data-testid="impersonation-workspace-state">
          {impersonatedBody}
        </Box>
      ) : (
        children
      )}
    </WorkspaceContext.Provider>
  );
}

export default WorkspaceContext;
